// src/encounter/lib/source-system.ts
// The coding system an order keeps from its source (PROD-2828, moved here for PROD-2831).
//
// One rule, used by the Settings order-set builder (`withSourceSystem`) and the A/P medication
// attach (the dose dialog, the edit seed): a coding with a blank system takes the system its source
// carried (the search pick, the catalogue row, or the order being edited) when the code is the same
// one. A system the coding already has wins, and a source with no system leaves it blank. Nothing is
// guessed: no default system, ever.

/** A coding, as much of it as the rule reads. */
export interface SourceCoding {
  code?: string | null;
  system?: string | null;
}

/** Codes compare without surrounding space or case. */
export const sameCode = (a: string | null | undefined, b: string | null | undefined): boolean =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

/** The source's system for this code: its trimmed system when the code is the same one, else ''. */
export function sourceSystemFor(code: string | null | undefined, source: SourceCoding | null | undefined): string {
  const system = (source?.system ?? '').trim();
  if (!system) return '';
  return sameCode(source?.code, code) ? system : '';
}

/**
 * The coding with the source's system filled in when its own is blank (the rule above). Returns the
 * SAME object when nothing changes, so callers can tell a no-op by identity.
 */
export function keepSourceSystem<T extends SourceCoding>(coding: T, source: SourceCoding | null | undefined): T {
  if ((coding.system ?? '').trim()) return coding;
  const system = sourceSystemFor(coding.code, source);
  return system ? { ...coding, system } : coding;
}
